import type { FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { DEFAULT_OEM_FILTERS } from 'constants/dealerFilters';
import { useUserProfile } from 'contexts/UserProfileContext';
import type { RoofTopI } from 'declarations/dto/roofTops';
import type {
  GetTicketsRequestParamsDTO,
  GetTicketsResponseDTO,
  GetTicketsTotalsByRoofTopResponseDTO,
} from 'declarations/dto/tickets';
import { SortingOrderE } from 'declarations/enums/common';
import { RoutePathsE } from 'declarations/enums/routes';
import { FILTER_PREFERENCES_VERSION, StorageE } from 'declarations/enums/storage';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import type {
  AgeFilterValuesE,
  AgentRoleFilterValuesE,
  AssigneeFilterValuesE,
  ParameterSortingValuesE,
  ScheduleFilterValuesE,
  StatusFilterValuesE,
} from 'declarations/enums/tickets';
import {
  CountByRoofTopParameterSortingValuesE,
  TicketsRefinementKeysE,
  TicketsViewModeE,
} from 'declarations/enums/tickets';
import type { SubFilterI } from 'declarations/interfaces/common';
import type { ScheduleFileI } from 'declarations/interfaces/scheduleFiles';
import type { TicketCountByRoofTopI } from 'declarations/interfaces/tickets';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { getOemFamilyIdByRoofTopId } from 'helpers/getOemFamilyByRoofTop';
import { getCurrentUserId } from 'helpers/user';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import {
  useFetchRooftopsList,
  useFetchTickets,
  useFetchTicketsCountByRoofTop,
} from 'hooks/api/useTickets';

export type TicketsContextT = {
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
  ticketsCount: number;
  ticketsCountByRoofTop: GetTicketsTotalsByRoofTopResponseDTO | null | undefined;
  mappedRooftopsList: TicketCountByRoofTopI[] | null | undefined;
  isTicketsFetchingError: boolean;
  fetchTicketsErrorMessage: string | undefined;
  fetchTicketsValidationErrorMessage: string | undefined;
  isTicketsFetchingCountByRoofTopError: boolean;
  isSuccessfullyFetchedTickets: boolean;
  isLoadingTickets: boolean;
  isLoadingTicketsCountByRoofTop: boolean;
  setTicketsViewMode: (ticketsViewMode: TicketsViewModeE) => void;
  ticketsViewMode: TicketsViewModeE;
  setTicketId: (id: string | null) => void;
  selectedTicketId: string | null;
  selectedTicketsIds: string[];
  selectedTicketsRooftopIds: string[];
  setSelectedTicketsIds: (id: string[]) => void;
  setSelectedTicketsRooftopIds: (id: string[]) => void;
  isNewTicketPopupOpened: boolean;
  isReassignTicketPopupOpened: boolean;
  isCloseTicketPopupOpened: boolean;
  isShareTicketPopupOpened: boolean;
  isImportSchedulePopupOpened: boolean;
  setNewTicketPopupVisibility: (isOpened: boolean) => void;
  setReassignTicketPopupVisibility: (isOpened: boolean) => void;
  setCloseTicketPopupVisibility: (isOpened: boolean) => void;
  setShareTicketPopupVisibility: (isOpened: boolean) => void;
  setImportSchedulePopupVisibility: (isOpened: boolean) => void;
  getSearchParam: (key: TicketsRefinementKeysE) => string | null;
  getAllSearchParam: (key: TicketsRefinementKeysE) => number[];
  updateSearchParam: (key: TicketsRefinementKeysE, value: string) => void;
  updateSearchParamsArray: (
    key: TicketsRefinementKeysE,
    value: (number | string | ScheduleFilterValuesE)[] | null
  ) => void;
  clearSearchParam: (keys: TicketsRefinementKeysE | TicketsRefinementKeysE[]) => void;
  isFetchingNextTicketsPage: boolean;
  isFetchingTickets: boolean;
  hasNextTicketsPage: boolean | undefined;
  fetchNextTicketsPage: () => void;
  fetchTicketsParams: GetTicketsRequestParamsDTO;
  selectedRoofTop: string;
  setSelectedRoofTop: (rooftop: string) => void;
  /** Fallback roofTop when none selected; used for categories/subcategories so OEM is always known */
  defaultRooftopId: string | undefined;
  /** OEM family ID for the selected dealer (selectedRoofTop ?? defaultRooftopId); used for category/subcategory filtering */
  oemFamilyId: string | undefined;
  activeScheduleCards: string[];
  setActiveScheduleCards: (cards: string[]) => void;
  activeScheduleFilters: ScheduleFilterValuesE[];
  setActiveScheduleFilters: (filters: ScheduleFilterValuesE[]) => void;
  shouldLoadMoreRooftops: boolean;
  hasNextRooftopsPage: boolean;
  fetchNextRooftopsPage: () => void;
  roofTopsList: RoofTopI[] | null;
  isRooftopsListFetchingError: boolean;
  isLoadingRoofTopsList: boolean;
  fetchRooftopsListErrorMessage: string | undefined;
  dealerSelectedRoofTopCount: number;
  setDealerSelectedRoofTopCount: (count: number) => void;
  dealerSelectedRooftopIds: string | null;
  setDealerSelectedRooftopIds: (id: string | null) => void;
  oemFilter: SubFilterI[];
  setOemFilter: (value: SubFilterI[] | ((prev: SubFilterI[]) => SubFilterI[])) => void;
  dealerGroupFilter: SubFilterI[];
  setDealerGroupFilter: (value: SubFilterI[] | ((prev: SubFilterI[]) => SubFilterI[])) => void;
  initialSelectedRooftopIds: string[];
  setInitialSelectedRooftopIds: (ids: string[]) => void;
  scheduleCategoryId: number;
  setScheduleCategoryId: (id: number) => void;
  countByRoofTopIdsOrderBy: string;
  setCountByRoofTopIdsOrderBy: (parameter: string) => void;
  countByRoofTopIdsOrder: string;
  setCountByRoofTopIdsOrder: (sortDirection: string) => void;
  activeTab: ActiveTabTypeE;
  setActiveTab: (tab: ActiveTabTypeE) => void;
  isEditMode: boolean;
  setEditMode: (value: boolean) => void;
  isSavingTickets: boolean;
  setIsSavingTickets: (value: boolean) => void;
  isScheduleTicketList: boolean;
  setIsScheduleTicketList: (value: boolean) => void;
  selectedSchedule: ScheduleFileI | null;
  setSelectedSchedule: (value: ScheduleFileI | null) => void;
  isViewScheduleFilesPopupOpened: boolean;
  setViewScheduleFilesPopupVisibility: (value: boolean) => void;
  ticketsTotalCount: number | undefined;
  schedulesTotalCount: number | undefined;
  openedTicketTabs: Record<string, string>;
  setOpenedTicketTab: (tabLabel: string, ticketId: string) => void;
  closeTicketTab: (tabLabel: string) => void;
  openedScheduleTicketTabs: Record<string, string>;
  setOpenedScheduleTicketTab: (tabLabel: string, ticketId: string) => void;
  closeScheduleTicketTab: (tabLabel: string) => void;
  ticketListScrollPosition: number;
  setTicketListScrollPosition: (position: number) => void;
  ticketListScrollContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  saveTicketListScrollPosition: () => void;
};

type TicketsProviderPropsT = { children: ReactNode };

const contextDefaultValue = {
  ticketsPages: null,
  ticketsCount: 0,
  ticketsCountByRoofTop: null,
  mappedRooftopsList: null,
  isTicketsFetchingError: false,
  fetchTicketsErrorMessage: undefined,
  fetchTicketsValidationErrorMessage: undefined,
  isTicketsFetchingCountByRoofTopError: false,
  isSuccessfullyFetchedTickets: false,
  isLoadingTickets: false,
  isLoadingTicketsCountByRoofTop: false,
  setTicketsViewMode: (): void => undefined,
  ticketsViewMode: TicketsViewModeE.List,
  setTicketId: (): void => undefined,
  setSelectedTicketsIds: (): void => undefined,
  setSelectedTicketsRooftopIds: (): void => undefined,
  selectedTicketId: '',
  selectedTicketsIds: [],
  selectedTicketsRooftopIds: [],
  isNewTicketPopupOpened: false,
  isReassignTicketPopupOpened: false,
  isCloseTicketPopupOpened: false,
  isShareTicketPopupOpened: false,
  isImportSchedulePopupOpened: false,
  setNewTicketPopupVisibility: (): void => undefined,
  setReassignTicketPopupVisibility: (): void => undefined,
  setCloseTicketPopupVisibility: (): void => undefined,
  setShareTicketPopupVisibility: (): void => undefined,
  setImportSchedulePopupVisibility: (): void => undefined,
  getSearchParam: (): string | null => null,
  getAllSearchParam: (): number[] => [],
  updateSearchParam: (): void => undefined,
  updateSearchParamsArray: (): void => undefined,
  clearSearchParam: (): void => undefined,
  isFetchingNextTicketsPage: false,
  isFetchingTickets: false,
  hasNextTicketsPage: false,
  fetchNextTicketsPage: (): void => undefined,
  fetchTicketsParams: {} as GetTicketsRequestParamsDTO,
  selectedRoofTop: '',
  setSelectedRoofTop: (): void => undefined,
  defaultRooftopId: undefined,
  oemFamilyId: undefined,
  activeScheduleCards: [],
  setActiveScheduleCards: (): void => undefined,
  activeScheduleFilters: [],
  setActiveScheduleFilters: (): void => undefined,
  shouldLoadMoreRooftops: false,
  hasNextRooftopsPage: false,
  fetchNextRooftopsPage: (): void => undefined,
  roofTopsList: null,
  isRooftopsListFetchingError: false,
  isLoadingRoofTopsList: false,
  fetchRooftopsListErrorMessage: '',
  dealerSelectedRoofTopCount: 0,
  setDealerSelectedRoofTopCount: (): void => undefined,
  dealerSelectedRooftopIds: '',
  setDealerSelectedRooftopIds: (): void => undefined,
  oemFilter: [],
  setOemFilter: (): void => undefined,
  dealerGroupFilter: [],
  setDealerGroupFilter: (): void => undefined,
  initialSelectedRooftopIds: [],
  setInitialSelectedRooftopIds: (): void => undefined,
  scheduleCategoryId: 0,
  setScheduleCategoryId: (): void => undefined,
  countByRoofTopIdsOrderBy: CountByRoofTopParameterSortingValuesE.RoofTopName,
  setCountByRoofTopIdsOrderBy: (): void => undefined,
  countByRoofTopIdsOrder: SortingOrderE.Asc,
  setCountByRoofTopIdsOrder: (): void => undefined,
  activeTab: ActiveTabTypeE.Tickets,
  setActiveTab: (_tab: ActiveTabTypeE): void => undefined,
  isEditMode: false,
  setEditMode: (): void => undefined,
  isSavingTickets: false,
  setIsSavingTickets: (): void => undefined,
  isScheduleTicketList: false,
  setIsScheduleTicketList: (): void => undefined,
  selectedSchedule: null,
  setSelectedSchedule: (): void => undefined,
  isViewScheduleFilesPopupOpened: false,
  setViewScheduleFilesPopupVisibility: (): void => undefined,
  ticketsTotalCount: 0,
  schedulesTotalCount: 0,
  openedTicketTabs: {},
  setOpenedTicketTab: (): void => undefined,
  closeTicketTab: (): void => undefined,
  openedScheduleTicketTabs: {},
  setOpenedScheduleTicketTab: (): void => undefined,
  closeScheduleTicketTab: (): void => undefined,
  ticketListScrollPosition: 0,
  setTicketListScrollPosition: (): void => undefined,
  ticketListScrollContainerRef: { current: null } as React.MutableRefObject<HTMLDivElement | null>,
  saveTicketListScrollPosition: (): void => undefined,
};

export const TicketsContext = createContext<TicketsContextT>(contextDefaultValue);

const getInitialOpenedTicketTabs = (): Record<string, string> => {
  try {
    const stored = localStorage.getItem(StorageE.TicketTabs);
    return stored ? (JSON.parse(stored) as Record<string, string>) : {};
  } catch {
    return {};
  }
};

const getInitialOpenedScheduleTicketTabs = (): Record<string, string> => {
  try {
    const stored = localStorage.getItem(StorageE.ScheduleTicketTabs);
    return stored ? (JSON.parse(stored) as Record<string, string>) : {};
  } catch {
    return {};
  }
};

const TicketsProvider: FC<TicketsProviderPropsT> = ({ children }): JSX.Element => {
  const { userProfile, isLoadingUserProfile } = useUserProfile();

  /**
   * Hooks
   */
  const [ticketsViewMode, setTicketsViewMode] = useState<TicketsViewModeE>(TicketsViewModeE.List);
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [openedTicketTabs, setOpenedTicketTabsState] = useState<Record<string, string>>(
    getInitialOpenedTicketTabs
  );
  const [openedScheduleTicketTabs, setOpenedScheduleTicketTabsState] = useState<
    Record<string, string>
  >(getInitialOpenedScheduleTicketTabs);
  const [selectedTicketsIds, setSelectedTicketsIds] = useState<string[]>([]);
  const [selectedTicketsRooftopIds, setSelectedTicketsRooftopIds] = useState<string[]>([]);
  const [isNewTicketPopupOpened, setNewTicketPopupVisibility] = useState<boolean>(false);
  const [isReassignTicketPopupOpened, setReassignTicketPopupVisibility] = useState<boolean>(false);
  const [isCloseTicketPopupOpened, setCloseTicketPopupVisibility] = useState<boolean>(false);
  const [isShareTicketPopupOpened, setShareTicketPopupVisibility] = useState<boolean>(false);
  const [isImportSchedulePopupOpened, setImportSchedulePopupVisibility] = useState<boolean>(false);
  const [isViewScheduleFilesPopupOpened, setViewScheduleFilesPopupVisibility] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRoofTop, setSelectedRoofTop] = useState<string>('');
  const navigate = useNavigate();
  const [activeScheduleCards, setActiveScheduleCards] = useState<string[]>([]);
  const [activeScheduleFilters, setActiveScheduleFilters] = useState<ScheduleFilterValuesE[]>([]);
  const [defaultRooftopId, setDefaultRooftopId] = useState<string>();
  const [dealerSelectedRoofTopCount, setDealerSelectedRoofTopCount] = useState(0);
  const [oemFilter, setOemFilter] = useState<SubFilterI[]>(DEFAULT_OEM_FILTERS);
  const [dealerGroupFilter, setDealerGroupFilter] = useState<SubFilterI[]>([]);
  const [dealerSelectedRooftopIds, setDealerSelectedRooftopIdsState] = useState<string | null>(
    () => {
      const userId = getCurrentUserId();
      if (!userId) return '';
      try {
        const stored = localStorage.getItem(`${StorageE.DealerCardSelectedRooftopIds}${userId}`);
        return stored != null && stored !== '' ? stored : '';
      } catch {
        return '';
      }
    }
  );

  const setDealerSelectedRooftopIds = useCallback((id: string | null): void => {
    setDealerSelectedRooftopIdsState(id);
  }, []);
  const [initialSelectedRooftopIds, setInitialSelectedRooftopIds] = useState<string[]>([]);
  const [scheduleCategoryId, setScheduleCategoryId] = useState<number>(0);
  const [countByRoofTopIdsOrder, setCountByRoofTopIdsOrder] = useState<string>(() => {
    const savedOrder = localStorage.getItem('countByRoofTopOrder');
    return savedOrder || SortingOrderE.Asc;
  });
  const [countByRoofTopIdsOrderBy, setCountByRoofTopIdsOrderBy] = useState<string>(() => {
    const savedOrderBy = localStorage.getItem('countByRoofTopOrderBy');
    return savedOrderBy || CountByRoofTopParameterSortingValuesE.RoofTopName;
  });
  const [activeTab, setActiveTab] = useState<ActiveTabTypeE>(
    (sessionStorage.getItem('activeTab') as ActiveTabTypeE) || ActiveTabTypeE.Tickets
  );
  const [ticketListScrollPosition, setTicketListScrollPosition] = useState(0);
  const ticketListScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const saveTicketListScrollPosition = useCallback((): void => {
    const el = ticketListScrollContainerRef.current;
    if (el) setTicketListScrollPosition(el.scrollTop);
  }, []);
  const [isEditMode, setEditMode] = useState(false);
  const [isSavingTickets, setIsSavingTickets] = useState(false);
  const [isScheduleTicketList, setIsScheduleTicketList] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleFileI | null>(null);

  /**
   * Variables
   */
  /** Fetch all dealers in one request (no pagination) */
  const ROOFTOPS_PAGE_SIZE = 10000;
  const location = useLocation();
  const hasRestoredFilterPreferencesRef = useRef(false);

  /** Filter keys to persist (excludes RONumber, SearchTerm, sorting, paging) */
  const FILTER_PREFERENCE_KEYS: TicketsRefinementKeysE[] = useMemo(
    (): TicketsRefinementKeysE[] => [
      TicketsRefinementKeysE.Status,
      TicketsRefinementKeysE.AssignedToMe,
      TicketsRefinementKeysE.TicketAge,
      TicketsRefinementKeysE.PastMonth,
      TicketsRefinementKeysE.Past30Days,
      TicketsRefinementKeysE.IsArchived,
      TicketsRefinementKeysE.AgentRoofTopRole,
      TicketsRefinementKeysE.Assignee,
      TicketsRefinementKeysE.Category,
      TicketsRefinementKeysE.SubCategory,
      TicketsRefinementKeysE.Schedule,
      TicketsRefinementKeysE.DMSScheduleAccounts,
      TicketsRefinementKeysE.RoofTopId,
      TicketsRefinementKeysE.ScheduleFileId,
      TicketsRefinementKeysE.WorkTypes,
    ],
    []
  );
  const currentPage = 1;
  const ticketsPerPage = 25;
  const fetchTicketsParams = useMemo(
    () => ({
      [TicketsRefinementKeysE.RoofTopId]:
        searchParams.get(TicketsRefinementKeysE.RoofTopId) || undefined,
      [TicketsRefinementKeysE.Status]:
        (searchParams.get(TicketsRefinementKeysE.Status) as StatusFilterValuesE) || undefined,
      [TicketsRefinementKeysE.AgentRoofTopRole]:
        (searchParams.get(TicketsRefinementKeysE.AgentRoofTopRole) as AgentRoleFilterValuesE) ||
        undefined,
      [TicketsRefinementKeysE.Schedule]:
        (searchParams.get(TicketsRefinementKeysE.Schedule) as ScheduleFilterValuesE) || undefined,
      [TicketsRefinementKeysE.TicketAge]:
        (searchParams.get(TicketsRefinementKeysE.TicketAge) as AgeFilterValuesE) || undefined,
      [TicketsRefinementKeysE.RONumber]:
        searchParams.get(TicketsRefinementKeysE.RONumber) || undefined,
      [TicketsRefinementKeysE.SearchTerm]:
        searchParams.get(TicketsRefinementKeysE.SearchTerm) || undefined,
      [TicketsRefinementKeysE.AssignedToMe]:
        !!searchParams.get(TicketsRefinementKeysE.AssignedToMe) || undefined,
      [TicketsRefinementKeysE.PastMonth]:
        !!searchParams.get(TicketsRefinementKeysE.PastMonth) || undefined,
      [TicketsRefinementKeysE.IsArchived]: ((): boolean | undefined => {
        const isArchivedParam = searchParams.get(TicketsRefinementKeysE.IsArchived);
        if (isArchivedParam === 'true') {
          return true;
        }
        if (isArchivedParam === 'false') {
          return false;
        }
        return undefined;
      })(),
      [TicketsRefinementKeysE.Past30Days]:
        !!searchParams.get(TicketsRefinementKeysE.Past30Days) || undefined,
      [TicketsRefinementKeysE.Parameter]:
        (searchParams.get(TicketsRefinementKeysE.Parameter) as ParameterSortingValuesE) ||
        undefined,
      [TicketsRefinementKeysE.Order]:
        (searchParams.get(TicketsRefinementKeysE.Order) as SortingOrderE) || undefined,
      [TicketsRefinementKeysE.CurrentPage]:
        (searchParams.get(TicketsRefinementKeysE.CurrentPage) as string) || currentPage.toString(),
      [TicketsRefinementKeysE.CountPerPage]:
        (searchParams.get(TicketsRefinementKeysE.CountPerPage) as string) ||
        ticketsPerPage.toString(),
      [TicketsRefinementKeysE.Category]: ((): string | undefined => {
        if (activeTab !== ActiveTabTypeE.Schedule) {
          return searchParams.get(TicketsRefinementKeysE.Category) || undefined;
        }
        if (scheduleCategoryId) {
          return String(scheduleCategoryId);
        }
        return searchParams.get(TicketsRefinementKeysE.Category) || undefined;
      })(),
      [TicketsRefinementKeysE.SubCategory]: ((): string | undefined => {
        const val = searchParams.get(TicketsRefinementKeysE.SubCategory) || '';
        return val === 'All' ? undefined : val || undefined;
      })(),
      [TicketsRefinementKeysE.RequestSubCategoryIds]:
        searchParams.get(TicketsRefinementKeysE.RequestSubCategoryIds) || undefined,
      [TicketsRefinementKeysE.IsSchedule]:
        searchParams.get(TicketsRefinementKeysE.IsSchedule) || undefined,
      [TicketsRefinementKeysE.DMSScheduleAccounts]:
        searchParams.get(TicketsRefinementKeysE.DMSScheduleAccounts) || undefined,
      [TicketsRefinementKeysE.Assignee]:
        (searchParams.get(TicketsRefinementKeysE.Assignee) as AssigneeFilterValuesE) || undefined,
      [TicketsRefinementKeysE.ScheduleFileId]:
        searchParams.get(TicketsRefinementKeysE.ScheduleFileId) || undefined,
      [TicketsRefinementKeysE.WorkTypes]:
        searchParams.get(TicketsRefinementKeysE.WorkTypes) || undefined,
    }),
    [searchParams, activeTab, scheduleCategoryId]
  );
  const dataKey = activeTab === ActiveTabTypeE.Schedule ? 'schedules' : 'tickets';
  /**
   * Api hooks
   */
  const ticketsResult = useFetchTickets(fetchTicketsParams);
  const roofTopsListResult = useFetchRooftopsList();
  const categoriesResult = useFetchTicketRequestCategories(
    true,
    selectedRoofTop ?? defaultRooftopId
  );
  const { refetch: refetchCategoriesResult } = categoriesResult;

  /**
   * Variables
   */
  const fetchTicketsErrorMessage = useMemo(
    () => getErrorMessage(ticketsResult.error),
    [ticketsResult.error]
  );
  const fetchRooftopsListErrorMessage = useMemo(
    () => getErrorMessage(roofTopsListResult.error),
    [roofTopsListResult.error]
  );

  const fetchTicketsValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(ticketsResult.error),
    [ticketsResult.error]
  );

  /**
   * Handlers
   */
  const getSearchParam = useCallback(
    (key: TicketsRefinementKeysE): string | null => searchParams.get(key),
    [searchParams]
  );

  const getAllSearchParam = useCallback(
    (key: TicketsRefinementKeysE): number[] =>
      searchParams.getAll(key).map((val): number => Number(val)),
    [searchParams]
  );

  const updateSearchParam = useCallback(
    (key: TicketsRefinementKeysE, value: string): void => {
      searchParams.set(key, value);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );
  const clearSearchParam = useCallback(
    (keys: TicketsRefinementKeysE | TicketsRefinementKeysE[]): void => {
      const next = new URLSearchParams(searchParams);
      const keysToDelete = Array.isArray(keys) ? keys : [keys];
      keysToDelete.forEach((key): void => next.delete(key));
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const updateSearchParamsArray = useCallback(
    (
      key: TicketsRefinementKeysE,
      valuesArray: (number | string | ScheduleFilterValuesE)[] | null
    ): void => {
      let queryString = window.location.pathname + '?';
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.forEach((value, paramKey): void => {
        if (paramKey !== key) {
          queryString += `${paramKey}=${encodeURIComponent(value)}&`;
        }
      });

      if (valuesArray && valuesArray.length > 0) {
        const valuesString = valuesArray.join(',');
        queryString += `${key}=${encodeURIComponent(valuesString)}`;
      } else {
        queryString = queryString.replace(/&$/, '');
      }
      navigate(queryString, { replace: true });
    },
    [navigate]
  );
  const fetchCountByRooftopsParams = useMemo(
    () => ({
      paging: {
        currentPage: currentPage,
        countPerPage: ROOFTOPS_PAGE_SIZE,
      },
      sorting: {
        parameter: countByRoofTopIdsOrderBy,
        order: countByRoofTopIdsOrder,
      },
      roofTopIds: dealerSelectedRooftopIds,
      agentRoofTopRole: getSearchParam(TicketsRefinementKeysE.AgentRoofTopRole),
    }),
    [
      countByRoofTopIdsOrder,
      countByRoofTopIdsOrderBy,
      currentPage,
      ROOFTOPS_PAGE_SIZE,
      dealerSelectedRooftopIds,
      getSearchParam,
    ]
  );

  const ticketsCountByRoofTopResult = useFetchTicketsCountByRoofTop(fetchCountByRooftopsParams);
  const { refetch } = ticketsCountByRoofTopResult;

  useEffect(() => {
    const tabDataPages = ticketsCountByRoofTopResult.data?.pages;
    if (!tabDataPages?.length) return;

    const tabCounts = tabDataPages.flatMap(page => page?.[dataKey]?.countByRoofTop || []);

    if (tabCounts.length > 0) {
      setDefaultRooftopId(tabCounts[0].roofTopId);
    }
  }, [ticketsCountByRoofTopResult.data, dataKey]);

  useEffect(() => {
    setSelectedRoofTop(getSearchParam(TicketsRefinementKeysE.RoofTopId) ?? '');
  }, [getSearchParam]);

  useEffect((): void => {
    if (isEditMode && selectedTicketsIds.length === 0) {
      setEditMode(false);
    }
  }, [isEditMode, selectedTicketsIds, setEditMode]);

  /** Restore saved filter preferences when on tickets list with no query (WarrQueue-style) */
  useEffect((): void => {
    const pathname = location.pathname;
    const isTicketsList =
      pathname === RoutePathsE.TICKETS_PAGE || pathname === `${RoutePathsE.TICKETS_PAGE}/`;
    if (!isTicketsList || hasRestoredFilterPreferencesRef.current) return;

    const hasAnyFilterParam = FILTER_PREFERENCE_KEYS.some(
      (key): boolean => searchParams.get(key) !== null
    );
    if (hasAnyFilterParam) {
      hasRestoredFilterPreferencesRef.current = true;
      return;
    }

    const userId = getCurrentUserId();
    if (!userId) return;

    try {
      const stored = localStorage.getItem(`${StorageE.TicketFilterPreferences}${userId}`);
      if (!stored) return;

      const parsed = JSON.parse(stored) as { version?: number; params?: Record<string, string> };
      if (parsed.version !== FILTER_PREFERENCES_VERSION || !parsed.params) return;

      const next = new URLSearchParams(window.location.search);
      Object.entries(parsed.params).forEach(([key, value]): void => {
        if (value != null && value !== '') next.set(key, value);
      });
      setSearchParams(next);
    } catch {
      // Ignore invalid stored data
    }
    hasRestoredFilterPreferencesRef.current = true;
  }, [FILTER_PREFERENCE_KEYS, location.pathname, searchParams, setSearchParams]);

  /** Persist filter preferences when they change (WarrQueue-style) */
  useEffect((): void => {
    const userId = getCurrentUserId();
    if (!userId) return;

    try {
      const params: Record<string, string> = {};
      FILTER_PREFERENCE_KEYS.forEach((key): void => {
        const value = searchParams.get(key);
        if (value != null && value !== '') params[key] = value;
      });
      const payload = { version: FILTER_PREFERENCES_VERSION, params };
      localStorage.setItem(`${StorageE.TicketFilterPreferences}${userId}`, JSON.stringify(payload));
    } catch {
      // Ignore storage errors
    }
  }, [FILTER_PREFERENCE_KEYS, searchParams]);

  useEffect(() => {
    if (isScheduleTicketList && selectedSchedule?.id) {
      updateSearchParam(
        TicketsRefinementKeysE.ScheduleFileId,
        selectedSchedule.id.toLocaleString()
      );
    }
    if (!isScheduleTicketList) {
      clearSearchParam(TicketsRefinementKeysE.ScheduleFileId);
      setSelectedSchedule(null);
    }
  }, [isScheduleTicketList, selectedSchedule, clearSearchParam, updateSearchParam]);

  /** Initialize dealer group filter from user profile when loaded */
  useEffect((): void => {
    if (userProfile?.accountPermissions && !isLoadingUserProfile) {
      setDealerGroupFilter(
        userProfile.accountPermissions.map(
          (acc): SubFilterI => ({
            value: acc.accountId,
            label: acc.account,
            checked: false,
          })
        )
      );
    }
  }, [userProfile, isLoadingUserProfile]);

  /** Persist dealer selection to localStorage (empty = all) */
  useEffect((): void => {
    const userId = getCurrentUserId();
    if (!userId) return;

    try {
      const value = dealerSelectedRooftopIds ?? '';
      localStorage.setItem(`${StorageE.DealerCardSelectedRooftopIds}${userId}`, value);
    } catch {
      // Ignore storage errors
    }
  }, [dealerSelectedRooftopIds]);

  useEffect(() => {
    localStorage.setItem('countByRoofTopOrder', countByRoofTopIdsOrder);
    localStorage.setItem('countByRoofTopOrderBy', countByRoofTopIdsOrderBy);
    refetch();
  }, [countByRoofTopIdsOrder, countByRoofTopIdsOrderBy, refetch]);

  useEffect(() => {
    if (ticketsCountByRoofTopResult.data && ticketsCountByRoofTopResult.data.pages.length > 0) {
      const countByRoofTops = ticketsCountByRoofTopResult.data.pages.flatMap(
        (page): TicketCountByRoofTopI[] => page?.[dataKey]?.countByRoofTop ?? []
      );

      if (countByRoofTops.length > 0 && countByRoofTops[0]) {
        const firstRoofTopId = countByRoofTops[0].roofTopId;
        setDefaultRooftopId(firstRoofTopId);
      }
    }
  }, [ticketsCountByRoofTopResult.data, dataKey]);

  useEffect(() => {
    localStorage.setItem(StorageE.TicketTabs, JSON.stringify(openedTicketTabs));
  }, [openedTicketTabs]);

  useEffect(() => {
    localStorage.setItem(StorageE.ScheduleTicketTabs, JSON.stringify(openedScheduleTicketTabs));
  }, [openedScheduleTicketTabs]);

  const setOpenedTicketTab = useCallback((tabLabel: string, ticketIdToAdd: string): void => {
    setOpenedTicketTabsState(prev => ({ ...prev, [tabLabel]: ticketIdToAdd }));
  }, []);

  const closeTicketTab = useCallback((tabLabel: string): void => {
    setOpenedTicketTabsState(prev => {
      const next = { ...prev };
      delete next[tabLabel];
      return next;
    });
  }, []);

  const setOpenedScheduleTicketTab = useCallback(
    (tabLabel: string, ticketIdToAdd: string): void => {
      setOpenedScheduleTicketTabsState(prev => ({ ...prev, [tabLabel]: ticketIdToAdd }));
    },
    []
  );

  const closeScheduleTicketTab = useCallback((tabLabel: string): void => {
    setOpenedScheduleTicketTabsState(prev => {
      const next = { ...prev };
      delete next[tabLabel];
      return next;
    });
  }, []);

  useEffect(() => {
    if (!defaultRooftopId) return;

    refetchCategoriesResult().then(({ data }) => {
      const scheduleCategory = data?.find(
        category => category.categoryName.toLowerCase() === 'schedule'
      );

      if (!scheduleCategory) return;

      setScheduleCategoryId(scheduleCategory.id);

      const rawCategoryParam = getSearchParam(TicketsRefinementKeysE.Category);
      if (!rawCategoryParam) return;

      const categoryIds = rawCategoryParam.split(',').map(id => Number(id.trim()));

      const hasInvalidCategory = categoryIds.some(id => isNaN(id) || id === 0);

      if (hasInvalidCategory) {
        updateSearchParamsArray(TicketsRefinementKeysE.Category, [scheduleCategory.id]);
      }
    });
  }, [defaultRooftopId, refetchCategoriesResult, getSearchParam, updateSearchParamsArray]);

  /** When on Schedule tab: clear Category from URL so categories filter is not applied.
   * Fetch params use scheduleCategoryId directly when on Schedule. */
  useEffect(() => {
    if (activeTab === ActiveTabTypeE.Schedule) {
      const rawCategoryParam = searchParams.get(TicketsRefinementKeysE.Category);
      if (rawCategoryParam) {
        updateSearchParamsArray(TicketsRefinementKeysE.Category, null);
      }
    }
  }, [activeTab, searchParams, updateSearchParamsArray]);

  /** When leaving Schedule tab: clear schedule-only Category from URL if present */
  useEffect(() => {
    if (scheduleCategoryId === 0) return;
    if (activeTab !== ActiveTabTypeE.Schedule) {
      const rawCategoryParam = searchParams.get(TicketsRefinementKeysE.Category);
      const categoryIds = rawCategoryParam
        ? rawCategoryParam
            .split(',')
            .map(id => Number(id.trim()))
            .filter(id => !isNaN(id))
        : [];
      const hasOnlyScheduleCategory =
        categoryIds.length === 1 && categoryIds[0] === scheduleCategoryId;
      if (hasOnlyScheduleCategory) {
        updateSearchParamsArray(TicketsRefinementKeysE.Category, null);
      }
    }
  }, [activeTab, scheduleCategoryId, searchParams, updateSearchParamsArray]);

  const combinedTicketsCountByRoofTop = useMemo(() => {
    if (ticketsCountByRoofTopResult.data?.pages) {
      return ticketsCountByRoofTopResult.data.pages.reduce<TicketCountByRoofTopI[]>(
        (acc, page): TicketCountByRoofTopI[] => acc.concat(page?.[dataKey]?.countByRoofTop || []),
        []
      );
    }
    return [];
  }, [ticketsCountByRoofTopResult.data, dataKey]);

  const roofTopsList = roofTopsListResult.data?.roofTops || null;
  const effectiveRoofTopForOem = selectedRoofTop || defaultRooftopId;
  const oemFamilyId = useMemo(
    (): string | undefined => getOemFamilyIdByRoofTopId(effectiveRoofTopForOem, roofTopsList),
    [effectiveRoofTopForOem, roofTopsList]
  );

  return (
    <TicketsContext.Provider
      value={{
        ticketsPages: ticketsResult.data?.pages ?? null,
        ticketsCount: ticketsResult.data?.pages[0].count || 0,
        isTicketsFetchingError: ticketsResult.isError,
        fetchTicketsErrorMessage,
        fetchTicketsValidationErrorMessage,
        isLoadingTickets: ticketsResult.isLoading || ticketsResult.isRefetching || isSavingTickets,
        isSuccessfullyFetchedTickets: ticketsResult.isSuccess,
        ticketsCountByRoofTop: ticketsCountByRoofTopResult.data?.pages[0],
        mappedRooftopsList: combinedTicketsCountByRoofTop,
        shouldLoadMoreRooftops: Boolean(
          ticketsCountByRoofTopResult.isRefetching &&
            ticketsCountByRoofTopResult.isFetchingNextPage &&
            ticketsCountByRoofTopResult.hasNextPage
        ),
        isLoadingTicketsCountByRoofTop: ticketsCountByRoofTopResult.isLoading,
        hasNextRooftopsPage: Boolean(ticketsCountByRoofTopResult.hasNextPage),
        fetchNextRooftopsPage: ticketsCountByRoofTopResult.fetchNextPage,
        isTicketsFetchingCountByRoofTopError: ticketsCountByRoofTopResult.isError,
        setTicketsViewMode,
        ticketsViewMode,
        setTicketId,
        setSelectedTicketsIds,
        setSelectedTicketsRooftopIds,
        selectedTicketId: ticketId,
        selectedTicketsIds,
        selectedTicketsRooftopIds,
        isNewTicketPopupOpened,
        isReassignTicketPopupOpened,
        isCloseTicketPopupOpened,
        isShareTicketPopupOpened,
        isImportSchedulePopupOpened,
        setNewTicketPopupVisibility,
        setReassignTicketPopupVisibility,
        setCloseTicketPopupVisibility,
        setShareTicketPopupVisibility,
        setImportSchedulePopupVisibility,
        getSearchParam,
        getAllSearchParam,
        updateSearchParam,
        updateSearchParamsArray,
        clearSearchParam,
        isFetchingNextTicketsPage: ticketsResult.isFetchingNextPage,
        isFetchingTickets: ticketsResult.isFetching,
        fetchNextTicketsPage: ticketsResult.fetchNextPage,
        hasNextTicketsPage: ticketsResult.hasNextPage,
        fetchTicketsParams,
        selectedRoofTop,
        setSelectedRoofTop,
        defaultRooftopId,
        oemFamilyId,
        activeScheduleCards,
        setActiveScheduleCards,
        activeScheduleFilters,
        setActiveScheduleFilters,
        roofTopsList,
        isRooftopsListFetchingError: false,
        isLoadingRoofTopsList: ticketsResult.isLoading,
        fetchRooftopsListErrorMessage: fetchRooftopsListErrorMessage,
        dealerSelectedRoofTopCount,
        setDealerSelectedRoofTopCount,
        setDealerSelectedRooftopIds,
        dealerSelectedRooftopIds,
        oemFilter,
        setOemFilter,
        dealerGroupFilter,
        setDealerGroupFilter,
        initialSelectedRooftopIds,
        setInitialSelectedRooftopIds,
        scheduleCategoryId,
        setScheduleCategoryId,
        countByRoofTopIdsOrder,
        setCountByRoofTopIdsOrder,
        countByRoofTopIdsOrderBy,
        setCountByRoofTopIdsOrderBy,
        activeTab,
        setActiveTab,
        isEditMode,
        setEditMode,
        isSavingTickets,
        setIsSavingTickets,
        isScheduleTicketList,
        setIsScheduleTicketList,
        selectedSchedule,
        setSelectedSchedule,
        isViewScheduleFilesPopupOpened,
        setViewScheduleFilesPopupVisibility,
        ticketsTotalCount: ticketsCountByRoofTopResult.data?.pages[0]?.tickets?.total,
        schedulesTotalCount: ticketsCountByRoofTopResult.data?.pages[0]?.schedules?.total,
        openedTicketTabs,
        setOpenedTicketTab,
        closeTicketTab,
        openedScheduleTicketTabs,
        setOpenedScheduleTicketTab,
        closeScheduleTicketTab,
        ticketListScrollPosition,
        setTicketListScrollPosition,
        ticketListScrollContainerRef,
        saveTicketListScrollPosition,
      }}
    >
      {children}
    </TicketsContext.Provider>
  );
};

const useTickets = (): TicketsContextT => useContext(TicketsContext);

export { TicketsProvider, useTickets };
